import React from 'react'

import useBrowserStorage from '../../../lib/hooks/useBrowserStorage'
import { ChatMessage } from '../chatMessage'

export const STORAGE_KEY = 'assistant-chat-history'

function isChatMessage(msg: unknown): msg is ChatMessage & { timestamp: string } {
  const m = msg as Record<string, unknown>
  return (
    typeof m?.id === 'string' &&
    typeof m?.text === 'string' &&
    (m?.sender === 'user' || m?.sender === 'assistant' || m?.sender === 'step') &&
    typeof m?.timestamp === 'string' &&
    !Number.isNaN(new Date(m.timestamp as string).getTime()) &&
    (m?.skill === undefined || typeof m?.skill === 'string')
  )
}

/**
 * Parses a JSON string of chat messages from sessionStorage into ChatMessage[].
 * Returns null on invalid JSON, non-array payloads, or empty arrays. Filters out
 * malformed entries. Step messages still in the "pending" status on load are
 * repaired to "error" — a pending step means the tool was interrupted by a
 * reload and will never complete.
 */
export function parseMessages(raw: string): ChatMessage[] | null {
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    const messages = parsed.filter(isChatMessage).map((msg) => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
      stepStatus:
        msg.sender === 'step' && msg.stepStatus === 'pending' ? ('error' as const) : msg.stepStatus
    }))
    return messages.length > 0 ? messages : null
  } catch {
    return null
  }
}

/**
 * Reads and writes chat messages from sessionStorage. Messages survive reloads
 * within a tab but not across tabs or restarts.
 */
const useChatMessages = (): [ChatMessage[], React.Dispatch<React.SetStateAction<ChatMessage[]>>] =>
  useBrowserStorage<ChatMessage[]>(STORAGE_KEY, [], {
    storage: sessionStorage,
    parse: parseMessages
  })

export default useChatMessages
